import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { STALE_TIME } from '@/constants/query.constants'
import { useAppDispatch, showLoader, hideLoader, showSnackbar } from '@/store'
import { getResponseMessage } from '@/utils'
import { permissionApi } from './permission.api'
import { permissionQueryKeys } from './permission.query-keys'
import type { PermissionKey } from './permission-keys'
import type { RolePermissionUpdateRequest } from './permission.types'

/*** GET /api/master/role */
export const useRoles = () =>
  useQuery({
    queryKey: permissionQueryKeys.roles(),
    queryFn: permissionApi.getRoles,
    staleTime: STALE_TIME.LONG,
  })

/*** GET /api/master/permission */
export const usePermissionCatalogue = () =>
  useQuery({
    queryKey: permissionQueryKeys.catalogue(),
    queryFn: permissionApi.getCatalogue,
    staleTime: STALE_TIME.LONG,
  })

/*** GET /api/master/role-permission */
export const useRolePermissionMatrix = () =>
  useQuery({
    queryKey: permissionQueryKeys.matrix(),
    queryFn: permissionApi.getMatrix,
    staleTime: STALE_TIME.BRIEF,
  })

/*** PUT /api/master/role-permission */
export const useUpdateRolePermissions = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (payload: RolePermissionUpdateRequest) => permissionApi.updateMatrix(payload),
    onMutate: () => {
      dispatch(showLoader())
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: permissionQueryKeys.matrix() })
      // The admin may have edited a role they hold themselves.
      queryClient.invalidateQueries({ queryKey: permissionQueryKeys.me() })
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Permissions saved'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to save permissions'),
        }),
      )
    },
  })
}

/**
 * The signed-in user's permissions, fetched once per session and shared by every gate.
 *
 * Refreshed on window focus, after a matrix save, and on any 403 (see axiosInstance), so a
 * permission an admin removes disappears from the UI within seconds without a re-login. The
 * backend enforces on its own; these gates are for the user's experience, not for security.
 *
 * Freshness: coming back to the tab re-reads the list, so an admin's change to the matrix shows up
 * about a second later with no reload and no sign-out. The 30-second window is a throttle, not a
 * delay — it caps window-switching at two calls a minute, and in practice the tab being returned
 * to was last read long before that. Working inside one window costs NO calls at all: refetching
 * on mount is deliberately off, because the sidebar, every route and every <Can> share this hook
 * and would otherwise fire a burst of identical requests on every page.
 *
 * Failure: with no list at all (first load against an older backend, or a network fault) every
 * gate opens rather than hiding the whole application — the server remains the gate either way.
 * But once a list has been read, a FAILED refresh keeps using it instead of opening everything:
 * the last known answer is a better guess than "allow all", and re-reading often would otherwise
 * turn a momentary blip into a wide-open UI.
 */
export const usePermissions = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: permissionQueryKeys.me(),
    queryFn: permissionApi.getMine,
    staleTime: STALE_TIME.BRIEF,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: false,
  })

  const granted = useMemo(() => new Set(data?.permissions ?? []), [data])
  const openEverything = isError && !data

  return useMemo(
    () => ({
      can: (key: PermissionKey) => openEverything || granted.has(key),
      canAny: (...keys: PermissionKey[]) => openEverything || keys.some((key) => granted.has(key)),
      roles: data?.roles ?? [],
      isLoading,
    }),
    [granted, data?.roles, isLoading, openEverything],
  )
}
